import {
  Box,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useState, useMemo } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import CustomText from "components/common/CustomText/CustomText";
import AddCustomFieldModal from "./AddCustomFieldModal";

import { colors } from "styles/colors";

import AddIcon from "icons/AddIcon";
import DeleteIcon from "icons/DeleteIcon";
import RegistriesIcon from "icons/RegistriesIcon";
import { hexWithOpacity } from "common/utils";

export default function CustomizedFields() {
  const { control } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "fields",
  });

  const [openModal, setOpenModal] = useState(false);

  /**
   * Only show user-created custom fields
   * Registry base fields may also exist in `fields`
   */
  const customFields = useMemo(
    () => fields.filter((field: any) => field?.value !== undefined),
    [fields]
  );

  const handleRemove = (id: string) => {
    const index = fields.findIndex((f) => f.id === id);
    if (index !== -1) remove(index);
  };

  const hasOptionsColumn = useMemo(() => {
    return customFields.some((field: any) => {
      const v = field?.value;

      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === "string") return v.trim().length > 0;

      return v !== null && v !== undefined;
    });
  }, [customFields]);

  return (
    <Box
      sx={{
        flex: 1,
        border: `1px solid ${colors.secondary}`,
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box px={2} py={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <CustomText
              text="Custom fields"
              fontSize={16}
              fontWeight={600}
            />
            <CustomText
              text="Customized fields on add case page will be arranged according to your order. The first entered fields will appear first."
              fontSize={12}
              color={colors.secondary}
              lineHeight={1}
            />
          </Box>

          <Tooltip title="Add Custom Field">
            <IconButton
              onClick={() => setOpenModal(true)}
              sx={{
                backgroundColor: colors.topBackground,
                width: 30,
                height: 30,
                borderRadius: "4px",
              }}
            >
              <AddIcon size={20} color={colors.primary} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Divider />

      {/* Table */}
      {customFields.length > 0 ? (<Box
        sx={{
          flex: 1,
          overflowY: "auto",
          minHeight: 0,
        }}
      >
        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
          }}
        >
          <Table stickyHeader sx={{ minWidth: 600 }}>
            <TableHead>
              <TableRow>
                <TableCell>Field name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Visible to others</TableCell>
                <TableCell>Required</TableCell>
                {hasOptionsColumn && <TableCell>Options</TableCell>}
                <TableCell />
              </TableRow>
            </TableHead>

            <TableBody>
              {customFields.map((row: any) => (
                <TableRow key={row.id}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.type}</TableCell>
                  <TableCell>{row.visible ? "Yes" : "No"}</TableCell>
                  <TableCell>{row.required ? "Yes" : "No"}</TableCell>
                  {hasOptionsColumn && <TableCell>{row.value}</TableCell>}

                  <TableCell width={60}>
                    <Tooltip title="Remove">
                      <IconButton
                        onClick={() => handleRemove(row.id)}
                      >
                        <DeleteIcon color={colors.error} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      ) : (
        <Stack flex={1} justifyContent={'center'} alignItems={'center'} spacing={2}>
          <RegistriesIcon size={60} color={hexWithOpacity(colors.secondary, 0.5)} />
          <CustomText
            text='Start by customizing your case form!'
            color={colors.secondary}
          />
        </Stack>
      )}

      {/* Modal */}
      <AddCustomFieldModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSubmit={(data: any) => {
          append(data);
          setOpenModal(false);
        }}
      />
    </Box>
  );
}